import { NgModule } from '@angular/core';
import {
  ExtraOptions,
  RouterModule,
  Routes,
  UrlMatchResult,
  UrlSegment,
} from '@angular/router';

import { LandingPageGetStartedComponent } from './landing-page/landing-page-get-started/landing-page-get-started.component';
import { LandingPageMainComponent } from './landing-page/landing-page-main/landing-page-main.component';
import { LandingRequestDemoPageComponent } from './landing-page/landing-request-demo-page/landing-request-demo-page.component';
import { EventLandingPageComponent } from './landing-page/event-landing-page/event-landing-page.component';
import { HomepageEventBannerResolver } from './core/resolvers/homepage-event-banner.resolver';

export function eventLandingPageRouteMatcher(
  segments: UrlSegment[]
): UrlMatchResult | null {
  const [events, eventId, slug] = segments;
  if (
    (segments.length !== 2 && segments.length !== 3) ||
    events?.path !== 'events' ||
    !/^[1-9]\d*$/.test(eventId?.path || '')
  ) {
    return null;
  }

  return {
    consumed: segments,
    posParams: {
      eventId,
      ...(slug ? { slug } : {}),
    },
  };
}

const routes: Routes = [
  // Canonical public home and standalone marketing routes.
  {
    path: '',
    component: LandingPageMainComponent,
    pathMatch: 'full',
    resolve: { homepageEventBanner: HomepageEventBannerResolver },
  },
  {
    path: 'request-demo',
    component: LandingRequestDemoPageComponent,
  },
  {
    path: 'get-started',
    component: LandingPageGetStartedComponent,
  },
  {
    matcher: eventLandingPageRouteMatcher,
    component: EventLandingPageComponent,
  },
  {
    path: 'category',
    loadChildren: () =>
      import('./template/template.module').then((m) => m.TemplateModule),
  },
  {
    path: 'profile',
    loadChildren: () =>
      import('./profile/profile.module').then((m) => m.ProfileModule),
  },
  {
    path: 'deduplication',
    loadChildren: () =>
      import('./deduplication/deduplication.module').then(
        (m) => m.DeduplicationModule
      ),
  },
  {
    path: 'search',
    pathMatch: 'full',
    redirectTo: '/representatives',
  },
  {
    path: 'spam-reports',
    loadChildren: () =>
      import('./spam-reports/spam-reports.module').then(
        (m) => m.SpamReportsModule
      ),
  },
  {
    path: 'election-details',
    loadChildren: () =>
      import('./election-details/election-details.module').then(
        (m) => m.ElectionDetailsModule
      ),
  },
  {
    path: 'verify-profiles',
    loadChildren: () =>
      import('./verify-profile/verify-profile.module').then(
        (m) => m.VerifyProfileModule
      ),
  },
  {
    path: 'claim-request',
    loadChildren: () =>
      import('./claim-request/claim-request.module').then(
        (m) => m.ClaimRequestModule
      ),
  },
  {
    path: 'dashboard',
    loadChildren: () =>
      import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
  },
  {
    path: 'matching-profile',
    loadChildren: () =>
      import('./matching-profile/matching-profile.module').then(
        (m) => m.MatchingProfileModule
      ),
  },
  {
    path: 'view-user-profile/:profileId',
    loadChildren: () =>
      import('./view-user-profile/view-user-profile.module').then(
        (m) => m.ViewUserProfileModule
      ),
  },
  {
    path: 'claim-profile/:profileId',
    loadChildren: () =>
      import('./claim-profile/claim-profile.module').then(
        (m) => m.ClaimProfileModule
      ),
  },
  {
    path: 'report-abuse',
    loadChildren: () =>
      import('./report-abuse/report-abuse.module').then(
        (m) => m.ReportAbuseModule
      ),
  },
  {
    path: 'account-settings',
    loadChildren: () =>
      import('./account-settings/account-settings.module').then(
        (m) => m.AccountSettingsModule
      ),
  },
  // HomepageModule remains lazy so its search, maps, and sharing providers do
  // not leak into the root injector. Its routing module owns the portal,
  // public content pages, and compatibility redirects.
  {
    path: '',
    loadChildren: () =>
      import('./homepage/homepage.module').then((m) => m.HomepageModule),
  },
];

const routerOptions: ExtraOptions = {
  scrollPositionRestoration: 'enabled',
  anchorScrolling: 'enabled',
};

@NgModule({
  imports: [RouterModule.forRoot(routes, routerOptions)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
